import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AutoSaveIndicator from '../../../../components/AutoSaveIndicator/AutoSaveIndicator';
import { createApplicationFormUpdateHandler } from '../../../../components/ApplicationInvite';
import ReviewShellBoundary from '../../../../components/ReviewWizard/ReviewShellBoundary';
import CountrySelectService from '../../../../components/CSC/CountrySelect/CountrySelectService';
import FormChangeConfirmation from '../../../../components/FormChangeConfirmation';
import { setValue, getAccountActivityRuleErrors } from '../../../../components/InputField/helper';
import useAutoSave from '../../../../hooks/useAutoSave';
import useFormReviewShell from '../../../../hooks/useFormReviewShell';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import { DialogContext } from '../../../../store/context/DialogContext';
import { useTranslation } from '../../../../store/context/TranslationContext';
import { doGET, doPUT } from '../../../../util/HttpUtil';
import { handleUtmFormSubmit } from '../../../../util/utm';
import EnhancedDueDiligenceContent from '../../../EDD/EDDContent';
import { withComputedHidePinCode } from '../../components/PincodeField/helper';
import pincodeService from '../../components/PincodeField/PincodeService';
import ConsoleAccordion from '../BPrime/components/ConsoleAccordion';
import { AskMoreMessage, ThankYou } from '../components';
import ApplicationDocRedo from '../components/IndividualLite/ApplicationDocRedo';
import ExtraQuestions from '../ExtraQuestions';
import { hasActiveAsks, hasPendingAsks } from '../askIdentity';
import { CONVERSIONS_FIELD_MAP } from '../IndividualPlus/constant';
import { areTransactionValuesSame, checkIsAskForInfo, convertArrayToDict, FORM_TYPE, getFormTypeLabel, formatActualTransactions, getIPlusWizardWhenRedo, getSelfdeclaredValues, isObjectNewTransactionsEmpty, scrollToTop, STEP_STATUS, STEPS, TRANSACTION_FIELDS } from '../IPlushelper';
import "../styles.scss";
import BusinessInformation from "./BusinessInformation";
import AboutBusiness from "./components/AboutBusiness";
import AssetsInvolvement from "./components/AssetsInvolvement";
import BPlusAccountActivityDeclaration from "./components/BPlusAccountActivityDeclaration";
import BusinessActivity from "./components/BusinessActivity";
import BusinessCompliance from "./components/BusinessCompliance";
import BusinessType from "./components/BusinessType";
import ComplianceDetails from "./components/ComplianceDetails";
import CorporateAccountUpgrade from "./components/CorporateAccountUpgrade";
import Documents from "./components/Documents";
import DocumentsPages from "./components/DocumentsPages";
import FundFamilyOffices from "./components/FundFamilyOffices";
import MoneyServicesBusiness from "./components/MoneyServicesBusiness";
import ReferralAndSource from "./components/ReferralAndSource";
import SelfDeclaration from "./components/SelfDeclaration";
import FormContainer, { FORM_CONTAINER_TYPE } from './FormContainer';
import { buildPostAutoSaveUpdatePayload, createAutoSaveFieldDefinitionMap, getAutoSaveProceedBlockedMessage, isAutoSaveEnabledForConfig, resolveAutoSaveDebounceMs } from '../autosave/shared';
import { createBusinessEddAutoSaveAdapter } from '../autosave/buildBusinessEddAutoSavePayload';
import { getBusinessTypeChecks } from '../BPrime/businessTypeUtils';
import { calculateInitialErrors, expandFieldInstances, fetchBusinessEddData, getBusinessReviewPermissions, getNextStep, handlePrev, isBusinessFieldEditable, movePrimaryUserToFront, WIZARD, WIZARD_NUM } from './helper';
import { SUBMIT_DOC_FLOW, validateSubmitDocuments } from '../submitDocumentValidation';


const COMPONENTS = {
  EnhancedDueDiligenceContent,
  BusinessInformation,
  BusinessActivity,
  BPlusAccountActivityDeclaration,
  BusinessCompliance,
  Documents,
  SelfDeclaration,
  ReferralAndSource,
  // BusinessPrime components
  BusinessType,
  AboutBusiness,
  MoneyServicesBusiness,
  AssetsInvolvement,
  FundFamilyOffices,
  ComplianceDetails,
  CorporateAccountUpgrade,
  DocumentsPages,
  Review: SelfDeclaration,
  // Other components
  ExtraQuestions,
  ApplicationDocRedo,
  ThankYou,
}

const ACCORDION_LABELS = {
  [WIZARD.BusinessInformation]: 'Applicant Information',
  [WIZARD.BusinessActivity]: 'Business Activity',
  [WIZARD.BPlusAccountActivityDeclaration]: 'Account Usage Declaration',
  [WIZARD.BusinessCompliance]: 'Compliance',
  [WIZARD.Documents]: 'Documents',
  [WIZARD.SelfDeclaration]: 'Self Declaration',
  [WIZARD.BusinessType]: 'Business Type',
  [WIZARD.AboutBusiness]: 'About Your Business',
  [WIZARD.MoneyServicesBusiness]: 'Money Services Business',
  [WIZARD.AssetsInvolvement]: 'Digital Asset Activity',
  [WIZARD.FundFamilyOffices]: 'Fund & Family Offices',
  [WIZARD.CorporateAccountUpgrade]: 'Corporate Account Upgrade',
  [WIZARD.ComplianceDetails]: 'Compliance Details',
  [WIZARD.DocumentsPages]: 'Additional Documents',
  [WIZARD.ExtraQuestions]: 'Requested Information',
};

const buildLocalId = () =>
  `local_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

const ensureDraftArrayItemIdentity = (items = [], identityFields = ['_id']) => (
  (Array.isArray(items) ? items : []).map((item) => {
    if (!_.isPlainObject(item)) {
      return item;
    }

    const hasStableIdentity = identityFields.some((field) => Boolean(item?.[field]));
    if (hasStableIdentity || item?._localId) {
      return item;
    }

    return {
      ...item,
      _isNew: true,
      _localId: buildLocalId(),
    };
  })
);

const resolveWizardFromStepParam = (rawStep) => {
  if (rawStep === undefined || rawStep === null) return null;
  const stepValue = String(rawStep).trim();
  if (!stepValue) return null;

  // Support numeric deep-links as well (e.g. ?step=5).
  const numeric = Number(stepValue);
  if (!Number.isNaN(numeric)) {
    const wizardKey = Object.keys(WIZARD_NUM || {}).find((key) => WIZARD_NUM[key] === numeric);
    if (wizardKey && WIZARD?.[wizardKey]) {
      return WIZARD[wizardKey];
    }
  }

  // Prefer matching by wizard value (case-insensitive).
  const byValue = Object.values(WIZARD || {}).find(
    (value) => String(value).toLowerCase() === stepValue.toLowerCase()
  );
  if (byValue) return byValue;

  // Also allow matching by wizard key.
  const byKey = Object.keys(WIZARD || {}).find(
    (key) => String(key).toLowerCase() === stepValue.toLowerCase()
  );
  if (byKey && WIZARD?.[byKey]) return WIZARD[byKey];

  // Common shorthands.
  const normalized = stepValue.replace(/[\s_-]/g, '').toLowerCase();
  if (normalized === 'docs' || normalized === 'document' || normalized === 'documents') {
    return WIZARD.Documents;
  }

  return null;
};

const ApplyBusinessPeriodicReview = ({ asLabel, formId, editMode = false, refreshId }) => {
  const IS_EDD = true;

  let { business_id } = useParams();
  const location = useLocation();
  let { showError, showConfirm } = useContext(DialogContext);
  const { t } = useTranslation();
  // ====================================================================================================================
  // ============================================= States for the component =============================================
  // ====================================================================================================================
  const [data, setData] = useState({});
  const [applicationForm, setApplicationForm] = useState({});
  const [isAskForInfo, setIsAskForInfo] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [editId, setEditID] = useState(business_id ?? formId);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [step, setStep] = useState({ wizard: WIZARD.BusinessInformation });
  const [errors, setErrors] = useState({});
  const [applicationSteps, setApplicationSteps] = useState({});
  const [cyclosFields, setCyclosFields] = useState({});
  const [iPlusFields, setIPlusFields] = useState([]);
  const [autoSaveConfig, setAutoSaveConfig] = useState(null);
  const [isFormEditable, setIsFormEditable] = useState(false);
  const [isUserReady, setIsUseready] = useState(false);
  const pageNumberRef = useRef(WIZARD_NUM.BusinessInformation);
  const [actualTransactions, setActualTransactions] = useState({});
  const [businessEddInfo, setBusinessEddInfo] = useState(null);
  const permissions = getBusinessReviewPermissions();
  const {
    reviewError,
    reviewLoading,
    reviewNavigation,
  } = useFormReviewShell({
    applicationId: editId,
    autoLoad: !asLabel && !editMode && Boolean(editId),
    fieldDefinitions: cyclosFields,
    formData: data,
    currentWizard: step?.wizard,
    onNavigateToWizard: (wizard) => {
      setStep({ wizard });
      pageNumberRef.current = WIZARD_NUM[wizard] ?? pageNumberRef.current;
      scrollToTop();
    },
    onPersistError: showError,
  });

  // Support deep-linking into a wizard step via query param (e.g. `?step=Documents`).
  useEffect(() => {
    const qsStep = new URLSearchParams(location.search).get('step');
    const wizardFromQuery = resolveWizardFromStepParam(qsStep);
    if (!wizardFromQuery) return;

    setStep((prev) => {
      if (prev?.wizard === wizardFromQuery) return prev;
      pageNumberRef.current = WIZARD_NUM[wizardFromQuery] ?? pageNumberRef.current;
      return { wizard: wizardFromQuery };
    });
  }, [location.search]);

  const shouldValidateField = useCallback(
    (field, { arrayIndexing, resolvedFieldPath, data: contextData }) => {
      if (step?.wizard !== WIZARD.BusinessInformation) {
        return true;
      }
      return isBusinessFieldEditable({
        data: contextData ?? data,
        resolvedFieldPath,
        arrayIndexing,
        permissions,
      });
    },
    [data, permissions, step?.wizard]
  );

  useEffect(() => {
    calculateInitialErrors({ data, step, iPlusFields, setErrors, errors, shouldValidateField });
  }, [JSON.stringify(data), step?.wizard, iPlusFields, shouldValidateField]);

  // ====================================================================================================================
  // ============================================ Handler functions for form ============================================
  // ====================================================================================================================
  /** * data change handler */
  const handleChange = (value) => {
    setData(value)
  };

  /** * Getting and setting JSON fields. */
  const getFields = async (group) => {
    try {
      if (Object.keys(pincodeService?.records)?.length === 0) {
        pincodeService.fetch().catch(console.error);
      }
      await CountrySelectService.fetch();
      // const cyclosResponse = { status: 200, data: FORMJSON }; // TODO: get the data from json
      const cyclosResponse = await doGET(`/api/cyclos/fields-json?group=${group}`);
      // const cyclosResponse = await doGET(`/api/cyclos/fields-json?group=${group}`);
      if (cyclosResponse.status === 200) {
        setAutoSaveConfig(cyclosResponse?.data?.autoSave || null);
        setIPlusFields(cyclosResponse?.data?.fields);
        // SYNC: Keep this periodic-review field normalization in sync with
        // `caas-console/src/pages/Form/forms/BusinessPeriodicReview/ApplyBusinessPeriodicReview.js`.
        const customFieldObject = Object.entries(createAutoSaveFieldDefinitionMap(cyclosResponse?.data?.fields)).reduce((acc, [key, field]) => {
          let normalizedField = field;
          if (field.field === "individuals[].mobilePhone") {
            normalizedField = {
              ...field,
              group: "BUSINESS_EDD",
              uiType: "PhoneNumWithValidation",
              validateOnly: true,
            };
          }
          if (field.field === "mobilePhone" || field.field === "landlinePhone") {
            normalizedField = {
              ...field,
              group: "BUSINESS_EDD",
              uiType: "PhoneNumWithValidation",
              validateOnly: true,
              phoneType: field.field === "landlinePhone" ? "landLine" : field.phoneType,
            };
          }
          if (field.field === "individuals[].email") {
            normalizedField = {
              ...field,
              uiType: "input",
            };
          }
          acc[key] = normalizedField;
          return acc;
        }, {});
        setCyclosFields(customFieldObject);
      }
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    getFields("BUSINESS_EDD");
    CountrySelectService.fetchCyclosCountryMaster();
  }, []);

  useEffect(() => { if (formId) setEditID(formId) }, [formId]);

  useEffect(() => {
    if (editMode) {
      setStep({ wizard: WIZARD.BusinessInformation });
    }
  }, [editMode])

  const handleUserReady = () => {
    setIsUseready(true);
    scrollToTop();
  }

  /* Fetch Actual Transactions */
  const fetchActualTransactions = async (cyclosUserId) => {
    if (!cyclosUserId) return;
    try {
      const actualTransactionsResponse = await doGET(`/api/cyclos/txn-data?cyclosUserId=${cyclosUserId}&application_id=${business_id}`);
      setActualTransactions(formatActualTransactions(actualTransactionsResponse?.data));
    } catch (error) {
      console.log(error);
    }
  }

  const fetchBusinessEddInfo = useCallback(async () => {
    if (!editId) return;
    try {
      const response = await doGET(`/api/business/edd-info?id=${editId}`);
      if (response?.status === 200) {
        setBusinessEddInfo(response?.data ?? null);
      } else {
        setBusinessEddInfo(null);
      }
    } catch (error) {
      console.error(error);
      setBusinessEddInfo(null);
    }
  }, [editId, step?.wizard]);

  useEffect(() => {
    if (applicationForm?.cyclosUserId)
      fetchActualTransactions(applicationForm?.cyclosUserId);
  }, [applicationForm?.cyclosUserId]);

  useEffect(() => {
    fetchBusinessEddInfo();
  }, [fetchBusinessEddInfo]);

  useEffect(() => {
    if (reviewNavigation?.hasActionableTasks) {
      setIsUseready(true);
      setIsFormEditable(true);
    }
  }, [reviewNavigation?.hasActionableTasks]);


  /** * Fetching the for details */
  const fetchDetail = async ({ stepProp, usernameProp, syncUi = true } = {}) => {
    let businessEddResponse = {}
    let applicationFormResponse = {}
    if (syncUi) {
      setDataLoading(true)
    }
    try {
      if (editId) {
        localStorage.setItem("formId", editId);
        /** * Fetching the detail of business */
        businessEddResponse = await fetchBusinessEddData(editId);
        /** * Fetching the details of application */
        const applicationResponse = await doGET(`/api/application-form/detail?id=${editId}&withExtension=true`)
        if (applicationResponse?.status == 200) {
          applicationFormResponse = applicationResponse?.data;
          if (syncUi && applicationFormResponse?.steps?.length) {

            /** * checking if any setp has status 2 (Incomplete) */
            const askInfoStep = checkIsAskForInfo(applicationFormResponse?.steps)

            /** * Formatting the steps into object for easy data reading */
            const stepsObj = convertArrayToDict(applicationFormResponse?.steps)
            setApplicationSteps(stepsObj);

            /** * if both form and documents are in submit, approved or rejected state then show thank you (nothing for user to do) */
            if (stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status > 0
              && !editMode
              && [1, 3, 4]?.includes(stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status)
              && stepsObj[STEPS.APPLICATION_FORM]?.status > 0
              && [1, 3, 4]?.includes(stepsObj[STEPS.APPLICATION_FORM]?.status)
            ) {
              setStep({ wizard: WIZARD.ThankYou });
            }


            /** * if any step has ask for info then we do application-redo */
            if (stepsObj[STEPS.APPLICATION_FORM]?.status > 0 && (Object.keys(askInfoStep ?? {}).length && !editMode)) {
              const askInfoStepData = { ...(askInfoStep ?? {}), isAskForInfo: true, step: getIPlusWizardWhenRedo(askInfoStep?.label), }
              /** * will take decision based in IsAskForInfo object */
              setIsAskForInfo(askInfoStepData)
              /** * setting step to thank you as we are rendering using IsAskForInfo */
              setStep({ wizard: WIZARD.ThankYou })
            } else {
              setIsAskForInfo(null)
            }
          }

          /** * application-form data */
          if (syncUi) {
            setApplicationForm(applicationResponse?.data)
          }

          businessEddResponse.individuals = movePrimaryUserToFront(businessEddResponse.individuals || []);
          if (businessEddResponse.individuals.length === 0) {
            const newIndividuals = [...businessEddResponse.individuals, { isPrimaryUser: true, _isNew: true, _localId: buildLocalId() }];
            // Initial shape fix-up should not create diffMap entries.
            businessEddResponse = setValue(businessEddResponse, 'individuals', newIndividuals, false);
          }

          const primaryUser = businessEddResponse.individuals?.[0];
          if (_.isPlainObject(primaryUser)) {
            primaryUser.isPrimaryUser = true; // Enforce first user is primary
            if (!primaryUser.email) primaryUser.email = businessEddResponse.email;
            if (!primaryUser.accessType) primaryUser.accessType = "NoGroup";
            if (!primaryUser.isAuthorizedUser) primaryUser.isAuthorizedUser = true;
          }
          businessEddResponse.individuals = businessEddResponse.individuals.map((individual, index) => {
            const nextIndividual = { ...(individual ?? {}) };
            nextIndividual.isControlPerson = nextIndividual?.isControlPerson === true;
            if (index === 0) {
              nextIndividual.isPrimaryUser = true;
              nextIndividual.isAuthorizedUser = true;
            }
            if (nextIndividual.isAuthorizedUser && !nextIndividual.accessType) {
              nextIndividual.accessType = "NoGroup";
            }
            return nextIndividual;
          });

          // 2. Ensure address and addressList are initialized
          if (!businessEddResponse.address) businessEddResponse.address = {};
          if (!Array.isArray(businessEddResponse.addressList)) {
            // Initial shape fix-up should not create diffMap entries.
            businessEddResponse = setValue(businessEddResponse, 'addressList', [], false);
          }

          // 3. Ensure REGISTERED and MAILING addresses exist in addressList
          const hasRegistered = businessEddResponse.addressList.some(addr => addr.type === "REGISTERED");
          const hasMailing = businessEddResponse.addressList.some(addr => addr.type === "MAILING");

          if (!hasRegistered) {
            const newAddressList = [{ type: "REGISTERED" }, ...businessEddResponse.addressList];
            // Initial shape fix-up should not create diffMap entries.
            businessEddResponse = setValue(businessEddResponse, 'addressList', newAddressList, false);
          }
          if (!hasMailing) {
            // Insert at index 1 if available, otherwise push
            const newAddressList = [...businessEddResponse.addressList];
            newAddressList.splice(1, 0, { type: "MAILING" });
            // Initial shape fix-up should not create diffMap entries.
            businessEddResponse = setValue(businessEddResponse, 'addressList', newAddressList, false);
          }

          const addressTypeToInclude = ["REGISTERED", "MAILING"];
          businessEddResponse.addressList = businessEddResponse.addressList.filter(addr =>
            addressTypeToInclude.includes(addr.type)
          );

          const principalAddress = businessEddResponse.address;
          const registeredAddress = businessEddResponse.addressList.find(addr => addr.type === "REGISTERED") || {};
          const mailingAddress = businessEddResponse.addressList.find(addr => addr.type === "MAILING") || {};

          // A simple compare function (you might need a more robust one from your old constants)
          const compareAddresses = (addr1, addr2) => {
            const fields = ['street', 'buildingNumber', 'city', 'state', 'pinCode', 'country'];
            if (!addr1 || !addr2) return false;
            return fields.every(field => addr1[field] === addr2[field] && addr1[field] !== undefined);
          };

          businessEddResponse.isRegisteredAddressSame = compareAddresses(principalAddress, registeredAddress);
          businessEddResponse.isMailingAddressSame = compareAddresses(principalAddress, mailingAddress);

          // 5. Set 'hidePinCode' flags based on country (self-heals a missing pinCode when the
          // country has a fixed default)
          businessEddResponse.address = withComputedHidePinCode(businessEddResponse.address);
          businessEddResponse.addressList = ensureDraftArrayItemIdentity(
            businessEddResponse.addressList.map(addr => withComputedHidePinCode(addr)),
            ['type']
          );
          businessEddResponse.individuals = ensureDraftArrayItemIdentity(
            businessEddResponse.individuals.map(ind => ({
              ...ind,
              address: withComputedHidePinCode(ind.address)
            })),
            ['aEId', 'cyclosUserId', 'cyclosUserName', '_id']
          );
          if (businessEddResponse?.hasEntity && (!Array.isArray(businessEddResponse.entities) || businessEddResponse.entities.length === 0)) {
            businessEddResponse.entities = [{}];
          }
          businessEddResponse.entities = ensureDraftArrayItemIdentity(
            businessEddResponse.entities || [],
            ['aEId', '_id']
          );


          const formData = {
            ...businessEddResponse,
            cyclosUserName: usernameProp ? usernameProp : applicationResponse?.data?.cyclosUserName,
            selfDeclaredValues: getSelfdeclaredValues(businessEddResponse),
            formType: applicationResponse?.data?.formType
          }
          if (syncUi) {
            calculateInitialErrors({
              data: formData,
              step: stepProp ? stepProp : step,
              iPlusFields,
              setErrors,
              errors: {},
              shouldValidateField,
            });
          }

          /** * business */
          if (syncUi) {
            setData(formData)
          }
          return formData;
        }
      }
    } catch (error) {
      // showError(error)
    } finally {
      if (syncUi) {
        setDataLoading(false)
        if (stepProp) setStep(stepProp)
        scrollToTop()
      }
    }
    return null;
  }

  useEffect(() => {
    fetchDetail({});
  }, [editId, refreshId]);

  // JSON owns wizard gating; these extra guards stay page-side because they depend on
  // live workflow status, editability, and periodic-review permissions.
  const isAutoSaveEnabled = !asLabel
    && !!editId
    && !!data?._id
    && !loading
    && !dataLoading
    && isUserReady
    && isFormEditable
    && (!editMode ? ![1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status) : true)
    && isAutoSaveEnabledForConfig({
      autoSaveConfig,
      step: step?.wizard,
    });
  const autoSaveDebounceMs = resolveAutoSaveDebounceMs(autoSaveConfig);

  const autoSaveAdapter = useMemo(
    () => createBusinessEddAutoSaveAdapter(cyclosFields, autoSaveConfig),
    [autoSaveConfig, cyclosFields]
  );

  const {
    autoSaveStatus,
    lastSavedAt,
    pendingChangesCount,
    pendingChanges,
    hasUnsavedChanges,
    isAutoSaveNavigationPending,
    flushAndPauseAutoSave,
    resumeAutoSave,
  } = useAutoSave({
    data,
    isEnabled: isAutoSaveEnabled,
    debounceMs: autoSaveDebounceMs,
    adapter: autoSaveAdapter,
    onDirtyUpdate: async () => await fetchDetail({ syncUi: false }),
    onDataReconcile: setData,
    onFieldPersisted: reviewNavigation?.commitFieldTask,
  });

  useUnsavedChangesWarning({
    when: hasUnsavedChanges || loading,
    isSaving: autoSaveStatus === 'saving' || autoSaveStatus === 'resyncing',
    pendingChangesCount,
  });

  const nextActionLoading = loading || isAutoSaveNavigationPending;
  const currentPageReviewTasks = reviewNavigation?.currentWizardActionableTasks || [];
  const hasCurrentWizardVisibleReviewTasks = Boolean(reviewNavigation?.currentWizardVisibleTasks?.length);

  const handleUpdate = async ({ nextStep, payload, isfetchDetail, isLastStep, skipUpdate }) => {
    let errorFlag = false;
    let latestData = data;
    let didResumeAutoSave = false;

    if ((payload ?? data)?._id) {
      setLoading(true)
      setHasError(false);
      const autoSaveResult = await flushAndPauseAutoSave();
      if (!autoSaveResult?.ok) {
        setLoading(false);
        setHasError(true);
        showError(getAutoSaveProceedBlockedMessage(autoSaveResult?.reason));
        return null;
      }
      latestData = autoSaveResult?.latestData || data;
      const refreshedCurrentPageReviewTasks = reviewNavigation?.peekCurrentWizardActionableTasks?.()
        || currentPageReviewTasks;
      const refreshedActionableReviewTasks = reviewNavigation?.peekActionableTasks?.()
        || reviewNavigation?.actionableTasks
        || [];
      if (nextStep && !isLastStep && !asLabel && !editMode && refreshedCurrentPageReviewTasks.length) {
        const blockedCurrentPageTask = reviewNavigation?.peekPreferredActionableTask?.(reviewNavigation?.currentWizard || "")
          || refreshedCurrentPageReviewTasks[0];
        resumeAutoSave(latestData);
        didResumeAutoSave = true;
        setLoading(false);
        showError("Please finish the remaining items on this page before continuing.");
        if (blockedCurrentPageTask) {
          reviewNavigation?.goToTask(blockedCurrentPageTask);
        }
        return { status: 400 };
      }
      if (isLastStep && !asLabel && !editMode && refreshedActionableReviewTasks.length) {
        const blockedActionableTask = reviewNavigation?.peekPreferredActionableTask?.()
          || refreshedActionableReviewTasks[0];
        resumeAutoSave(latestData);
        didResumeAutoSave = true;
        setLoading(false);
        showError("Please complete all requested items before submitting.");
        if (blockedActionableTask) {
          reviewNavigation?.goToTask(blockedActionableTask);
        }
        return { status: 400 };
      }
      let updatedPayload = { ...buildPostAutoSaveUpdatePayload(latestData, payload) };

      if (step.wizard === WIZARD.BPlusAccountActivityDeclaration) {
        const { success, fieldsToUpdate, error } = await askForConversionValueUpdate();
        if (!success) {
          setLoading(false);
          showError(error);
          return;
        }
        fieldsToUpdate.forEach(field => {
          updatedPayload[field] = true;
        });
      }


      if (updatedPayload.individuals?.length) {
        updatedPayload.individuals = updatedPayload.individuals.map((individual, index) => {
          const nextIndividual = { ...(individual ?? {}) };
          nextIndividual.isControlPerson = nextIndividual?.isControlPerson === true;
          if (index === 0) {
            nextIndividual.isPrimaryUser = true;
            nextIndividual.isAuthorizedUser = true;
            if (!nextIndividual.email && updatedPayload.email) {
              nextIndividual.email = updatedPayload.email;
            }
            if (!nextIndividual.accessType) {
              nextIndividual.accessType = "NoGroup";
            }
          } else if (nextIndividual.isAuthorizedUser && !nextIndividual.accessType) {
            nextIndividual.accessType = "NoGroup";
          }
          return nextIndividual;
        });
      }

      // If no entities, clear the array
      if (!updatedPayload.hasEntity) {
        updatedPayload.entities = [];
      }

      // Remove empty country codes
      const keysToOmit = [];
      if (!updatedPayload.landlinePhone) keysToOmit.push('landlineCountryCode');
      if (!updatedPayload.mobilePhone) keysToOmit.push('mobileCountryCode');

      // Remove empty strings and specified keys
      updatedPayload = _.omit(
        _.omitBy(updatedPayload, value => value === ""),
        keysToOmit
      );

      /** * TODO :  deleting empty strings attributes */

      delete updatedPayload?.selfDeclaredValues;
      let submissionData = {
        ...latestData,
        formType: applicationForm?.formType,
      };

      try {
        /** * if not asLabel or not skip update then go inside this block */
        if (!asLabel && !skipUpdate) {
          /** * update API called */
          const response = await doPUT(`/api/business/update`, updatedPayload)
          if (response?.status == 200) {
            let businessResponse = response?.data ?? {};

            businessResponse.individuals = movePrimaryUserToFront(businessResponse.individuals || []);
            const nextData = {
              ...businessResponse,
              selfDeclaredValues: getSelfdeclaredValues(businessResponse),
              formType: applicationForm?.formType,
            };
            setData(nextData);
            submissionData = nextData;
            resumeAutoSave(nextData);
            didResumeAutoSave = true;
          } else {
            errorFlag = true
            resumeAutoSave(null);
            didResumeAutoSave = true;
          }
        }

        /** * if isLastStep and not asLabel then submit the form */
        if (isLastStep && !asLabel && !editMode) {
          const documentValidation = await validateSubmitDocuments({
            flow: SUBMIT_DOC_FLOW.BUSINESS_PERIODIC_REVIEW,
            recordId: editId,
            data: submissionData,
            applicationForm,
            formType: applicationForm?.formType,
          });

          if (!documentValidation?.isValid) {
            setHasError(true);
            showError(documentValidation?.errors?.[0]?.error || "Please upload all required documents before submitting.");
            errorFlag = true;
            return { status: 400 };
          }

          const submitResponse = await doGET(`/api/business/submit?id=${editId}&markFormSubmit=true&markDocSubmit=true`);
          // try {
          //   await doGET(`/api/application-doc/cert/beneficial-owners?form_id=${editId}`);
          // } catch (error) {
          //   console.log("Certificate generation error:", error);
          // }

          if (submitResponse?.status == 200) {
            await reviewNavigation?.markVisibleTasksResponded?.({ quiet: true });
            handleUtmFormSubmit({
              formType: "BUSINESS_EDD",
              utmPayload: {
                utm_source: applicationForm?.extension?.utm_source,
                utm_medium: applicationForm?.extension?.utm_medium,
                utm_campaign: applicationForm?.extension?.utm_campaign,
                utm_content: applicationForm?.extension?.utm_content,
                utm_term: applicationForm?.extension?.utm_term,
              }
            })

            await new Promise(resolve => setTimeout(resolve, 1000));
            /** if there is next step then go to it */
            if (isfetchDetail) {
              pageNumberRef.current = nextStep ? WIZARD_NUM[nextStep.wizard] : WIZARD_NUM.BusinessInformation;
              await fetchDetail(nextStep ? { stepProp: nextStep } : {});
              setLoading(false);
            }
          } else {
            errorFlag = true;
          }
        } else {
          /** go to next step / page */
          if (nextStep) {
            setStep(nextStep);
            pageNumberRef.current = WIZARD_NUM[nextStep.wizard];
          } else {
            pageNumberRef.current = pageNumberRef.current + 1;
          }
          scrollToTop()
          setLoading(false);
        }
      } catch (error) {
        setHasError(true);
        showError(error)
        errorFlag = true
        resumeAutoSave(null);
        didResumeAutoSave = true;
      }
      finally {
        if (skipUpdate && !didResumeAutoSave) {
          resumeAutoSave(null);
        }
        setLoading(false)
      }
    } else {
      /** * if not id present then just simply go to next step */
      if (nextStep) fetchDetail({ stepProp: nextStep })
      resumeAutoSave(null);
    }

    return errorFlag ? { status: 400 } : { status: 200 }
  };

  // ====================================================================================================================
  // =============================================== Conversions Handlers ===============================================
  // ====================================================================================================================

  const askForConversionValueUpdate = async () => {
    const fieldsToUpdate = [];
    if (
      data?.hasCoinConversionPlan === false && (
        data?.Number_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
        data?.Amount_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
        data?.Number_Of_Monthly_Stablecoin_Conversions_Payments > 0 ||
        data?.Amount_Of_Monthly_Stablecoin_Conversions_Payments > 0
      )) {
      fieldsToUpdate.push("hasCoinConversionPlan");
    }
    if (
      data?.hasCryptoConversionPlan === false && (
        data?.Count_Of_Monthly_Cryptocurrency_Conversions > 0 || data?.Amount_Of_Monthly_Cryptocurrency_Conversions > 0
      )) {
      fieldsToUpdate.push("hasCryptoConversionPlan");
    }
    if (fieldsToUpdate.length === 0) {
      return { success: true, fieldsToUpdate: [], error: "" };
    } else {
      const title = fieldsToUpdate.map(field => CONVERSIONS_FIELD_MAP[field]).join(" and ");
      const response = await showConfirm({
        title: t(`Do you want to update ${title} to Yes?`),
      });
      if (response) return { success: true, fieldsToUpdate, error: '' };
      else return { success: false, fieldsToUpdate, error: `You have to update ${title} to Yes or change their values to 0 to proceed.` };
    }
  }

  // ====================================================================================================================
  // =========================================== Handler functions for Banner ===========================================
  // ====================================================================================================================
  /** * To show or hide the bottom banner asking user to change any information */
  useEffect(() => {
    if (reviewNavigation?.hasActionableTasks || hasCurrentWizardVisibleReviewTasks) {
      setIsFormEditable(true);
      return;
    }

    /** If the wizard page is of form type first 5-6 pages then we show banner */
    if (![
      WIZARD.EnhancedDueDiligenceContent,
      WIZARD.ExtraQuestions,
      WIZARD.ApplicationDocRedo,
      WIZARD.Documents,
      WIZARD.DocumentsPages
    ].includes(step?.wizard)) {
      if (!isUserReady) {
        setIsFormEditable(true);
        return
      }
      /** 
       * But if the wizard is BPlusAccountActivityDeclaration (transaction table) then: 
       * 1. If the transaction values are same as self declared values then we show banner
       * 2. If all self declared values are present and transaction values are empty then we show banner
       * else we hide the banner and make form editable
      * */
      if (step.wizard === WIZARD.BPlusAccountActivityDeclaration) {
        if (areTransactionValuesSame(data, data?.selfDeclaredValues)) {
          setIsFormEditable(false);
        } else if (
          (Object.keys(data?.selfDeclaredValues).length === TRANSACTION_FIELDS.length) &&
          isObjectNewTransactionsEmpty(data)
        ) {
          setIsFormEditable(false);
        } else {
          setIsFormEditable(true);
        }
      } else if (step.wizard === WIZARD.SelfDeclaration) {
        if (applicationForm?.formType == FORM_TYPE.BusinessPlus) {
          setIsFormEditable(true);
        } else {
          setIsFormEditable(false);
        }
      } else {
        setIsFormEditable(false);
      }
    } else {
      setIsFormEditable(true);
    }
  }, [hasCurrentWizardVisibleReviewTasks, reviewNavigation?.hasActionableTasks, step?.wizard, isUserReady])

  /**
   * If any field is required then we do not have to ask for any changes as the user needs to fill the required field to proceed.
   * If this is not done then user may bypass the required field.
  */
  useEffect(() => {
    if (!Array.isArray(iPlusFields) || step?.wizard === WIZARD.BPlusAccountActivityDeclaration) return;

    const stepFields = iPlusFields.filter(f => f?.wizard === step?.wizard);

    for (const field of stepFields) {
      // Expand array fields to check each instance
      const instances = expandFieldInstances(field, data);

      for (const { resolvedFieldPath } of instances) {
        const fieldError = _.get(errors, resolvedFieldPath);
        if (fieldError) {
          setIsFormEditable(true);
          return;
        }
      }
    }
  }, [errors, iPlusFields, step?.wizard, data]);

  const bannerConfig = useMemo(() => {
    const isActivityDeclaration = step.wizard === WIZARD.BPlusAccountActivityDeclaration;
    return {
      title: isActivityDeclaration
        ? ""
        : "Please confirm if any of the previously provided information has changed?",

      noButtonText: isActivityDeclaration
        ? "No changes required"
        : "No changes",

      yesButtonText: isActivityDeclaration
        ? "I want to update my expected activity"
        : "Edit",

      btnWidth: isActivityDeclaration
        ? "350px"
        : "180px"
    };

  }, [step.wizard])

  /** * skipping the step without any changes and saving */
  const handleSkipChanges = async ({ nextStep }) => {
    /* for BPlusAccountActivityDeclaration (transaction table) wizard we clone the self declared values when we skip and remove clear the transaction fields from diffMap */
    if (step.wizard === WIZARD.BPlusAccountActivityDeclaration) {
      const updatedDiffMap = { ...data?.diffMap || {} };
      TRANSACTION_FIELDS.forEach(field => {
        delete updatedDiffMap[field];
      });
      const updatedData = {
        ...data,
        ...data?.selfDeclaredValues,
        diffMap: updatedDiffMap,
      };
      setData(updatedData);

      const crossFieldErrors = getAccountActivityRuleErrors(updatedData, {
        depositLocationFields: ["Deposit_Locations"],
        paymentLocationFields: ["Payment_Locations"]
      });
      if (crossFieldErrors.length) {
        setHasError(true);
        setIsFormEditable(true);
        const firstError = crossFieldErrors[0];
        showError(firstError.message);
        const mapped = crossFieldErrors.reduce((acc, err) => {
          const fields = Array.isArray(err.activityFields) && err.activityFields.length ? err.activityFields : [err.field];
          fields.forEach(field => _.set(acc, field, err.message));
          return acc;
        }, {});
        setErrors((prev) => _.merge({}, prev, mapped));
        return;
      }

      await handleUpdate({ nextStep, payload: updatedData });
    }
    else {
      if (nextStep) {
        setStep(nextStep)
        scrollToTop()
      }
    }
  };

  const handleAcceptEditChanges = (e) => {
    e?.preventDefault();
    setIsFormEditable(true);
    /* for BPlusAccountActivityDeclaration (transaction table) if we require to change then we clear the filled transaction value from data state */
    if (step?.wizard === WIZARD.BPlusAccountActivityDeclaration) {
      setData(prev => {
        const updated = { ...prev }; // shallow copy
        TRANSACTION_FIELDS.forEach(field => {
          delete updated[field];
          if (!updated?.selfDeclaredValues[field])
            delete updated?.diffMap[field];
          else updated.diffMap[field] = updated?.selfDeclaredValues[field]; // If the self declared value for field is present then set the diffMap value for that field to its selfDeclaredValue
        });
        return updated;
      });
    }
  }

  const isLoading = dataLoading || !Object.keys(cyclosFields)?.length || !CountrySelectService?.cyclosCountryMaster?.length;
  const CurrentComponent = COMPONENTS[step.wizard];
  const hasActionableReviewTasks = Boolean(reviewNavigation?.hasActionableTasks);
  const hasVisibleReviewTasks = Boolean(reviewNavigation?.hasVisibleTasks);
  const hasResolvedNoActionableReviewTasks = !reviewLoading && !hasActionableReviewTasks;
  const applicationFormStepStatus = applicationSteps[STEPS.APPLICATION_FORM]?.status;
  const documentVerificationStepStatus = applicationSteps[STEPS.DOCUMENT_VERIFICATION]?.status;
  const isDocumentRedoRequired = applicationFormStepStatus > STEP_STATUS.DRAFT
    && [STEP_STATUS.DRAFT, STEP_STATUS.INCOMPLETE].includes(documentVerificationStepStatus);

  const renderInFormLayout = (content, showReviewWizard = false) => (
    <FormContainer
      asLabel={asLabel}
      editMode={editMode}
      form_id={data?._id}
      formContainerType={FORM_CONTAINER_TYPE.EDD}
      title={IS_EDD ? `Periodic Review - ${getFormTypeLabel(applicationForm?.formType, applicationForm?.type)}` : `Business Plus`}
      isLoading={isLoading}
      applicationForm={applicationForm}
      applicationFormId={editId}
      formData={data}
      onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
    >
      <div className='form-content-body-content flex-1'>
        <div className='d-flex justify-content-end mb-3'>
          <AutoSaveIndicator
            status={autoSaveStatus}
            lastSavedAt={lastSavedAt}
            pendingChangesCount={pendingChangesCount}
            pendingChanges={pendingChanges}
          />
        </div>
        <ReviewShellBoundary
          reviewNavigation={reviewNavigation}
          loading={reviewLoading}
          error={reviewError}
          showReviewWizard={showReviewWizard}
        >
          {content}
        </ReviewShellBoundary>
      </div>
    </FormContainer>
  );


  // ====================================================================================================================
  // ================================================= Component Rendering ==============================================
  // ====================================================================================================================
  /** document-redo after submitting application */
  if (!asLabel && !isLoading && !editMode) {
    if (isDocumentRedoRequired) {
      return renderInFormLayout(
        <ApplicationDocRedo
          hideContainerStyle
          recordId={editId}
          asLabel={asLabel}
          isForEDD={IS_EDD}
          formId={editId}
          formType={FORM_TYPE.Business}
          type={IS_EDD ? `Periodic Review - ${getFormTypeLabel(applicationForm?.formType, applicationForm?.type)}` : `Business Plus`}
        />
      );
    }
  }

  const requiredKycUboIds = businessEddInfo?.uboIdsForKYC ?? [];
  const isAnyKycRequired = Array.isArray(requiredKycUboIds) ? requiredKycUboIds.length > 0 : true;

  /** if we are ahead of last step and my ask for info is incomplete then show questions */
  if (!asLabel && !isLoading && !editMode) {
    if (WIZARD_NUM[step?.wizard] > (hasPendingAsks(data?.asks) ? WIZARD_NUM.ApplicationDocRedo : WIZARD_NUM.KYC_VERIFICATION)
      && isAskForInfo?.status == STEP_STATUS.INCOMPLETE
      && isAskForInfo?.label == STEPS.APPLICATION_FORM
    ) {
      return renderInFormLayout(
        <AskMoreMessage
          hideContainerStyle
          data={data}
          title={IS_EDD ? `Periodic Review - ${getFormTypeLabel(applicationForm?.formType, applicationForm?.type)}` : `Business Plus`}
          remark={(data?.remark ?? "")}
          loading={nextActionLoading}
          onNext={() => { setStep({ wizard: WIZARD.ExtraQuestions }) }}
        />
      );
    }
  }

  /** if the step is greater than application-redo (in case of askForInfo) or KYC then we have no next page so render ThankYou page */
  if (!asLabel && !isLoading && !editMode) {
    if (WIZARD_NUM[step?.wizard] > (hasPendingAsks(data?.asks) ? WIZARD_NUM.APPLICATION_FORM_REDO : WIZARD_NUM.KYC_VERIFICATION)) {
      return <ThankYou
        application_id={editId}
        type={applicationForm?.formType}
        isForEdd
        isKycRequired={isAnyKycRequired}
        requiredUboIds={Array.isArray(requiredKycUboIds) ? requiredKycUboIds : undefined}
        applicationForm={applicationForm}
        formData={data}
      />
    }
  }

  /** rendering the steps as accordian */
  const renderAsAccordion = () => {
    const baseAccordionProps = {
      decrementStep: () => { setStep(handlePrev(step?.wizard, applicationForm, data)) },
      recordId: business_id,
      cyclosFields,
      iPlusFields,
      data,
      handleChange,
      pageNum: pageNumberRef.current,
      loading: nextActionLoading,
      step,
      wizard: step?.wizard,
      handleUpdate,
      setErrors,
      setData,
      asLabel: !editMode,
      editMode: editMode,
      hasError,
      setHasError,
      applicationForm,
      errors,
      setLoading,
      hideButtons: true,
      actualTransactions,
      formType: applicationForm?.formType,
      onOtpVerify: () => { fetchDetail({ usernameProp: data?.cyclosUserName }) },
      isForEDD: true,
    };

    const wizardSequence = [
      WIZARD.BusinessInformation,
      WIZARD.BusinessActivity,
      WIZARD.BPlusAccountActivityDeclaration,
      WIZARD.BusinessCompliance,
      WIZARD.SelfDeclaration,
    ];

    const isFormTypeBPrime = applicationForm?.formType === FORM_TYPE.BusinessPrime;
    if (isFormTypeBPrime) {
      const { msbCheck, cryptoExchangeCheck, fundAndFamilyCheck, accountUpgradeCheck } = getBusinessTypeChecks(data?.businesses);

      wizardSequence.push(WIZARD.BusinessType, WIZARD.AboutBusiness);

      if (msbCheck || cryptoExchangeCheck) {
        wizardSequence.push(WIZARD.MoneyServicesBusiness);
      }

      if (cryptoExchangeCheck) {
        wizardSequence.push(WIZARD.AssetsInvolvement);
      }

      if (fundAndFamilyCheck) {
        wizardSequence.push(WIZARD.FundFamilyOffices);
      }

      if (accountUpgradeCheck) {
        wizardSequence.push(WIZARD.CorporateAccountUpgrade);
      }

      if (msbCheck || cryptoExchangeCheck || fundAndFamilyCheck) {
        wizardSequence.push(WIZARD.ComplianceDetails);
      }

    }

    if (hasActiveAsks(data?.asks)) {
      wizardSequence.push(WIZARD.ExtraQuestions);
    }

    const uniqueWizards = wizardSequence.filter((wizardKey, index, arr) => arr.indexOf(wizardKey) === index);

    const extraQuestionsProps = {
      dataLoading: loading || dataLoading,
      formId: business_id,
      formType: applicationForm?.formType,
      isForEDD: true,
      onSubmit: async () => {
        await fetchDetail({});
        window.location.reload();
      },
      questionIds: data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? [],
    };

    const steps = uniqueWizards.map((wizardKey) => {
      const Component = COMPONENTS[wizardKey];
      if (!Component) return null;

      const label = ACCORDION_LABELS[wizardKey] || wizardKey;
      const sharedProps = {
        ...baseAccordionProps,
        wizard: wizardKey,
        step: { wizard: wizardKey },
        label,
      };

      if (wizardKey === WIZARD.ExtraQuestions) {
        return (
          <Component
            key={wizardKey}
            {...sharedProps}
            {...extraQuestionsProps}
          />
        );
      }

      return (
        <Component
          key={wizardKey}
          {...sharedProps}
        />
      );
    }).filter(Boolean);

    return <ConsoleAccordion steps={steps} />;
  };

  // ====================================================================================================================
  // ================================================== What is EDD Page ================================================
  // ====================================================================================================================
  /** showing content */
  if (
    !isUserReady
    && !isLoading
    && hasResolvedNoActionableReviewTasks
    && WIZARD_NUM[step?.wizard] != WIZARD_NUM.APPLICATION_FORM_REDO
  ) {
    return (
      <FormContainer
        asLabel={asLabel}
        editMode={editMode}
        form_id={data?._id}
        formContainerType={FORM_CONTAINER_TYPE.EDD}
        title={IS_EDD ? `Periodic Review - ${getFormTypeLabel(applicationForm?.formType, applicationForm?.type)}` : `Business Plus`}
        applicationForm={applicationForm}
        applicationFormId={editId}
        formData={data}
        onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
      >
        <EnhancedDueDiligenceContent handleNext={() => handleUserReady()} />
      </FormContainer>
    )
  }

  const currentComponentDisabled = () => {
    /* For wizards that can show FormChangeConfirmation, consider isFormEditable state */
    if (![
      WIZARD.EnhancedDueDiligenceContent,
      WIZARD.ExtraQuestions,
      WIZARD.ApplicationDocRedo,
      WIZARD.Documents,
      WIZARD.DocumentsPages,
      WIZARD.ThankYou,
    ].includes(step?.wizard)) {
      if (hasCurrentWizardVisibleReviewTasks) {
        return asLabel;
      }

      return asLabel || !isFormEditable || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status) && !editMode;
    } else {
      return asLabel;
    }
  }

  const getPropsForCurrentComponent = () => {
    const commoProps = {
      asLabel: currentComponentDisabled(),
      hideButtons: asLabel || !isFormEditable,
      data,
      handleUpdate,
      setHasError,
      isForEDD: IS_EDD,
      handlePrev: () => { setStep(handlePrev(step?.wizard, applicationForm, data)) },
      formType: applicationForm?.formType,
      loading,
    }

    if ([
      WIZARD.BusinessInformation,
      WIZARD.BusinessActivity,
      WIZARD.BPlusAccountActivityDeclaration,
      WIZARD.BusinessCompliance,
      WIZARD.SelfDeclaration,
      // BusinessPrime wizards
      WIZARD.BusinessType,
      WIZARD.AboutBusiness,
      WIZARD.MoneyServicesBusiness,
      WIZARD.AssetsInvolvement,
      WIZARD.FundFamilyOffices,
      WIZARD.ComplianceDetails,
      WIZARD.CorporateAccountUpgrade,
      WIZARD.Review,
    ].includes(step.wizard)) {
      return {
        ...commoProps,
        hasError,
        loading,
        setLoading,
        cyclosFields,
        errors,
        setErrors,
        iPlusFields,
        wizard: step.wizard,
        handleChange,
        setData,
        actualTransactions,
        applicationForm,
        onOtpVerify: () => { fetchDetail({ usernameProp: data?.cyclosUserName }) },
        uiConfig: {
          showInputColumns: !IS_EDD | isFormEditable || [
            WIZARD.EnhancedDueDiligenceContent,
            WIZARD.ExtraQuestions,
            WIZARD.ApplicationDocRedo,
            WIZARD.Documents,
            WIZARD.DocumentsPages,
            WIZARD.SelfDeclaration,
            WIZARD.ReferralAndSource,
          ].includes(step?.wizard)
        }
      }
    }
    switch (step.wizard) {
      case WIZARD.Documents:
      case WIZARD.DocumentsPages:
        return {
          ...commoProps,
          editId,
          recordId: editId,
          isAskForInfo: isAskForInfo?.status == STEP_STATUS.INCOMPLETE,
          remark: data?.remark,
          wizard: step.wizard,
          applicationForm
        }

      case WIZARD.ReferralAndSource:
        return {
          ...commoProps,
          hasError,
          loading,
          setLoading,
          cyclosFields,
          errors,
          setErrors,
          iPlusFields,
          wizard: step.wizard,
          handleChange,
          setData,
          editId
        }

      case WIZARD.ExtraQuestions:
        return {
          dataLoading: loading || dataLoading,
          formType: applicationForm?.formType,
          isForEDD: IS_EDD,
          data, asLabel: currentComponentDisabled(),
          questionIds: data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? [],
          formId: editId,
          onSubmit: async () => {
            await fetchDetail({})
            window.location.reload();
          }
        }
      case WIZARD.ApplicationDocRedo:
        return {
          type: IS_EDD ? `Periodic Review - ${getFormTypeLabel(applicationForm?.formType, applicationForm?.type)}` : `Business Plus`,
          formType: applicationForm?.formType,
          formId: editId,
          isForEDD: IS_EDD,
          hideContainerStyle: true,
          asLabel: currentComponentDisabled(),
          recordId: editId
        }
      case WIZARD.ThankYou:
        return {
          application_id: data?._id,
          type: applicationForm?.formType,
          isForEDD: true,
          isKycRequired: isAnyKycRequired,
          requiredUboIds: Array.isArray(requiredKycUboIds) ? requiredKycUboIds : undefined,
        }
      default:
        break;
    }
  }

  /** Wrapper container to show componenent based on current wizard */
  return (
    <FormContainer
      asLabel={asLabel}
      editMode={editMode}
      form_id={data?._id}
      formContainerType={FORM_CONTAINER_TYPE.EDD}
      title={IS_EDD ? `Periodic Review - ${getFormTypeLabel(applicationForm?.formType, applicationForm?.type)}` : `Business Plus`}
      isLoading={isLoading}
      applicationForm={applicationForm}
      applicationFormId={editId}
      formData={data}
      onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
    >
      {/* 
      <pre className="json-pretty">
        {formatJson(data)}
      </pre> */}

      {/* {JSON.stringify(step, null, 2)} */}

      <div className='form-content-body-content flex-1'>
        <div className='d-flex justify-content-end mb-3'>
          <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} pendingChangesCount={pendingChangesCount} pendingChanges={pendingChanges} />
        </div>
        {asLabel ? renderAsAccordion() : (
          <ReviewShellBoundary
            reviewNavigation={reviewNavigation}
            loading={reviewLoading}
            error={reviewError}
            showReviewWizard={hasVisibleReviewTasks && ![
              WIZARD.ExtraQuestions,
              WIZARD.ApplicationDocRedo,
              WIZARD.KYCVerification,
              WIZARD.ThankYou,
            ].includes(step?.wizard)}
          >
            {CurrentComponent ? (
              <CurrentComponent
                {...getPropsForCurrentComponent()}
              />
            ) : null}
          </ReviewShellBoundary>
        )}
      </div>

      {/* Confirmation for form changes */}
      {IS_EDD && !isFormEditable && ![
        WIZARD.EnhancedDueDiligenceContent,
        WIZARD.ExtraQuestions,
        WIZARD.ApplicationDocRedo,
        WIZARD.Documents,
        WIZARD.DocumentsPages,
      ].includes(step?.wizard) && hasResolvedNoActionableReviewTasks && !hasCurrentWizardVisibleReviewTasks ?
        <FormChangeConfirmation
          onClickYes={handleAcceptEditChanges}
          onClickNo={(e) => {
            e?.preventDefault();
            handleSkipChanges({ nextStep: getNextStep(step?.wizard, applicationForm, data) })
          }}
          loading={nextActionLoading || dataLoading}
          config={bannerConfig}
        /> :
        null}


    </FormContainer>

  );
};

export default observer(ApplyBusinessPeriodicReview);
